import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import { Shield } from 'lucide-react';
import { DetailedItem, FloatingLabelInput } from '../../../../../../ui';
import { useEditableForm } from '../hooks/useEditableForm';
import { SettingsCard } from './SettingsCard';

interface SecuritySettingsData {
  twoFactorAuth: boolean;
  ssoEnabled: boolean;
  passwordMinLength: number;
  passwordRequireSpecialChars: boolean;
  passwordRequireNumbers: boolean;
  passwordRequireUppercase: boolean;
  sessionTimeout: number;
  ipWhitelisting: boolean;
  allowedIPs: string;
  loginAttempts: number;
  lockoutDuration: number;
  encryptionLevel: 'standard' | 'enhanced' | 'maximum';
}

interface ISecuritySettings {
  onSave?: (data: SecuritySettingsData) => Promise<void> | void;
}

export const SecuritySettings: React.FC<ISecuritySettings> = ({ onSave }) => {
  const initialData: SecuritySettingsData = {
    twoFactorAuth: true,
    ssoEnabled: false,
    passwordMinLength: 8,
    passwordRequireSpecialChars: true,
    passwordRequireNumbers: true,
    passwordRequireUppercase: true,
    sessionTimeout: 30,
    ipWhitelisting: false,
    allowedIPs: '192.168.1.0/24\n10.0.0.0/8',
    loginAttempts: 5,
    lockoutDuration: 15,
    encryptionLevel: 'enhanced',
  };

  const { isEditing, formData, isSaving, handleEditToggle, handleSave, handleInputChange } = useEditableForm({
    initialData,
    onSave: async (data) => {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      if (onSave) {
        await onSave(data);
      }
    },
  });

  const handleToggleChange = (field: keyof SecuritySettingsData) => {
    handleInputChange(field, !formData[field]);
  };

  const getEncryptionDisplayText = (level: string) => {
    switch (level) {
      case 'standard':
        return 'Standard (AES-128)';
      case 'enhanced':
        return 'Enhanced (AES-256)';
      case 'maximum':
        return 'Maximum (AES-256 + HSM)';
      default:
        return level;
    }
  };

  return (
    <SettingsCard
      title="Security Settings"
      description="Configure security policies and authentication requirements"
      icon={<Shield size={20} />}
      isEditing={isEditing}
      isSaving={isSaving}
      onEditToggle={handleEditToggle}
      onSave={handleSave}
    >
      {isEditing ? (
        <Stack spacing={3.5}>
          {/* Authentication */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
              Authentication
            </Typography>
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={formData.twoFactorAuth}
                    onChange={() => handleToggleChange('twoFactorAuth')}
                  />
                }
                label={<Typography sx={{ fontSize: '0.875rem' }}>Two-Factor Authentication (2FA)</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={formData.ssoEnabled}
                    onChange={() => handleToggleChange('ssoEnabled')}
                  />
                }
                label={<Typography sx={{ fontSize: '0.875rem' }}>Single Sign-On (SSO)</Typography>}
              />
            </Stack>
          </Box>

          {/* Password Policy */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
              Password Policy
            </Typography>
            <Box sx={{ maxWidth: { xs: '100%', sm: 300 }, mb: 2 }}>
              <FloatingLabelInput
                label="Minimum Password Length"
                type="number"
                value={formData.passwordMinLength.toString()}
                onChange={(e) => handleInputChange('passwordMinLength', parseInt(e.target.value) || 8)}
              />
            </Box>
            <Stack spacing={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={formData.passwordRequireSpecialChars}
                    onChange={() => handleToggleChange('passwordRequireSpecialChars')}
                  />
                }
                label={<Typography sx={{ fontSize: '0.875rem' }}>Require Special Characters</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={formData.passwordRequireNumbers}
                    onChange={() => handleToggleChange('passwordRequireNumbers')}
                  />
                }
                label={<Typography sx={{ fontSize: '0.875rem' }}>Require Numbers</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={formData.passwordRequireUppercase}
                    onChange={() => handleToggleChange('passwordRequireUppercase')}
                  />
                }
                label={<Typography sx={{ fontSize: '0.875rem' }}>Require Uppercase Letters</Typography>}
              />
            </Stack>
          </Box>

          {/* Session & Access Control */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
              Session & Access Control
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2.5, mb: 2 }}>
              <FloatingLabelInput
                label="Session Timeout (minutes)"
                type="number"
                value={formData.sessionTimeout.toString()}
                onChange={(e) => handleInputChange('sessionTimeout', parseInt(e.target.value) || 30)}
              />
              <FloatingLabelInput
                label="Max Login Attempts"
                type="number"
                value={formData.loginAttempts.toString()}
                onChange={(e) => handleInputChange('loginAttempts', parseInt(e.target.value) || 5)}
              />
              <FloatingLabelInput
                label="Lockout Duration (minutes)"
                type="number"
                value={formData.lockoutDuration.toString()}
                onChange={(e) => handleInputChange('lockoutDuration', parseInt(e.target.value) || 15)}
              />
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={formData.ipWhitelisting}
                  onChange={() => handleToggleChange('ipWhitelisting')}
                />
              }
              label={<Typography sx={{ fontSize: '0.875rem' }}>Enable IP Whitelisting</Typography>}
            />

            {formData.ipWhitelisting && (
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.primary', mb: 0.75 }}>
                  Allowed IP Addresses / Ranges
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  size="small"
                  value={formData.allowedIPs}
                  onChange={(e) => handleInputChange('allowedIPs', e.target.value)}
                  placeholder={'192.168.1.0/24\n10.0.0.0/8\n203.0.113.5'}
                  helperText="Enter one IP address or CIDR range per line"
                  InputProps={{ sx: { borderRadius: '8px', fontFamily: 'monospace', fontSize: '0.8125rem' } }}
                />
              </Box>
            )}
          </Box>

          {/* Encryption */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
              Encryption
            </Typography>
            <FormControl fullWidth size="small">
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.primary', mb: 0.75 }}>
                Encryption Level
              </Typography>
              <Select
                value={formData.encryptionLevel}
                onChange={(e) => handleInputChange('encryptionLevel', e.target.value)}
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="standard">Standard (AES-128)</MenuItem>
                <MenuItem value="enhanced">Enhanced (AES-256)</MenuItem>
                <MenuItem value="maximum">Maximum (AES-256 + HSM)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Stack>
      ) : (
        <Stack spacing={3}>
          {/* Authentication Status */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
              Authentication Status
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.75,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderRadius: '8px',
                  bgcolor: 'action.hover',
                  borderColor: 'divider',
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: formData.twoFactorAuth ? 'success.main' : 'error.main',
                    }}
                  />
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.primary' }}>
                    Two-Factor Authentication
                  </Typography>
                </Stack>
                <Chip
                  size="small"
                  label={formData.twoFactorAuth ? 'Enabled' : 'Disabled'}
                  color={formData.twoFactorAuth ? 'success' : 'error'}
                  variant="outlined"
                />
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  p: 1.75,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderRadius: '8px',
                  bgcolor: 'action.hover',
                  borderColor: 'divider',
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: formData.ssoEnabled ? 'success.main' : 'text.disabled',
                    }}
                  />
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.primary' }}>
                    Single Sign-On
                  </Typography>
                </Stack>
                <Chip
                  size="small"
                  label={formData.ssoEnabled ? 'Enabled' : 'Disabled'}
                  color={formData.ssoEnabled ? 'success' : 'default'}
                  variant="outlined"
                />
              </Paper>
            </Box>
          </Box>

          {/* Password Policy */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
              Password Policy
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', bgcolor: 'action.hover', borderColor: 'divider' }}>
                <DetailedItem label="Minimum Length" text={`${formData.passwordMinLength} characters`} />
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', bgcolor: 'action.hover', borderColor: 'divider' }}>
                <DetailedItem
                  label="Requirements"
                  text={
                    [
                      formData.passwordRequireSpecialChars && 'Special chars',
                      formData.passwordRequireNumbers && 'Numbers',
                      formData.passwordRequireUppercase && 'Uppercase',
                    ]
                      .filter(Boolean)
                      .join(', ') || 'None'
                  }
                />
              </Paper>
            </Box>
          </Box>

          {/* Session & Access Control */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
              Session & Access Control
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', bgcolor: 'action.hover', borderColor: 'divider' }}>
                <DetailedItem label="Session Timeout" text={`${formData.sessionTimeout} minutes`} />
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', bgcolor: 'action.hover', borderColor: 'divider' }}>
                <DetailedItem label="Max Login Attempts" text={formData.loginAttempts.toString()} />
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px', bgcolor: 'action.hover', borderColor: 'divider' }}>
                <DetailedItem label="Lockout Duration" text={`${formData.lockoutDuration} minutes`} />
              </Paper>
            </Box>

            <Paper
              variant="outlined"
              sx={{
                p: 1.75,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderRadius: '8px',
                bgcolor: 'action.hover',
                borderColor: 'divider',
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: formData.ipWhitelisting ? 'success.main' : 'text.disabled',
                  }}
                />
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.primary' }}>
                  IP Whitelisting
                </Typography>
              </Stack>
              <Chip
                size="small"
                label={formData.ipWhitelisting ? 'Active' : 'Inactive'}
                color={formData.ipWhitelisting ? 'success' : 'default'}
                variant="outlined"
              />
            </Paper>

            {formData.ipWhitelisting && (
              <Box sx={{ mt: 2 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: '8px',
                    bgcolor: 'rgba(59, 130, 246, 0.04)',
                    borderColor: 'rgba(59, 130, 246, 0.2)',
                  }}
                >
                  <DetailedItem
                    label="Allowed IP Ranges"
                    text={formData.allowedIPs.split('\n').join(', ')}
                    helpertext="Only these IP addresses can access the system"
                  />
                </Paper>
              </Box>
            )}
          </Box>

          {/* Encryption */}
          <Box>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: '8px',
                bgcolor: 'rgba(16, 185, 129, 0.04)',
                borderColor: 'rgba(16, 185, 129, 0.2)',
              }}
            >
              <DetailedItem
                label="Encryption Level"
                text={getEncryptionDisplayText(formData.encryptionLevel)}
                helpertext="All data is encrypted using industry-standard protocols"
              />
            </Paper>
          </Box>
        </Stack>
      )}
    </SettingsCard>
  );
};
